// "Today's Rafiki is ready": an optional daily reminder in the iPhone and Android apps.
//
// Local notifications only (@capacitor/local-notifications): the phone itself schedules them, so
// there is no push server, no device token and nothing leaves the device. It does nothing on the
// website, the portals or Discord, and nothing in an app build that doesn't include the native
// plugin (Capacitor.isPluginAvailable is false there), so it is safe to ship ahead of the app
// update that adds the plugin.
//
// Never asked on first launch: after the player's first Daily Rafiki, the result card offers
// "Get a nudge when tomorrow's Rafiki is ready?" with three times of day and "Not now". Only a
// time-of-day tap asks the phone for permission. The game menu has an on/off switch.
//
// We schedule the next week of reminders (one a day, at the chosen local time) and refresh them
// whenever the app opens and whenever a Daily is finished. A day whose Daily has already been
// played on this device gets no reminder.
import { logEvent } from "./analytics";
import { t } from "./i18n";
import { isPortal } from "./portal";
import { hasPlayedDaily, utcDate } from "./redux/daily";
import { store } from "./store";

export const DAILY_REMINDER_ENABLED = true;
export const REMINDER_KEY = "rafiki.reminder";
export const REMINDER_DAYS = 7;
export const REMINDER_ID_BASE = 7001;
export const REMINDER_CHANNEL = "daily";

export type Slot = "morning" | "lunch" | "evening";
// [hour, minute] (a plain number[]: the build's old TypeScript parser can't read tuple types)
export const SLOTS: Record<Slot, number[]> = {
  morning: [9, 0],
  lunch: [12, 30],
  evening: [18, 30],
};

export interface ReminderPrefs {
  on: boolean;
  slot: Slot;
  asked: boolean; // the result-card offer has been shown and answered (or dismissed)
}

const DEFAULT_PREFS: ReminderPrefs = { on: false, slot: "morning", asked: false };

export const readPrefs = (): ReminderPrefs => {
  try {
    const v = JSON.parse(store.getItem(REMINDER_KEY) || "null");
    if (!v || typeof v !== "object") return { ...DEFAULT_PREFS };
    const slot: Slot = v.slot in SLOTS ? v.slot : "morning";
    return { on: v.on === true, slot, asked: v.asked === true };
  } catch (e) {
    return { ...DEFAULT_PREFS };
  }
};

export const savePrefs = (p: ReminderPrefs) => {
  try {
    store.setItem(REMINDER_KEY, JSON.stringify(p));
  } catch (e) {}
};

// The local times to remind at: the next REMINDER_DAYS days at the slot's time, skipping times
// already past and any day whose Daily (by the UTC date at that moment, which is how the Daily
// changes) has been played. Pure, tested in dailyReminder.test.js.
export const planReminders = (now: Date, slot: Slot, played: (utcDay: string) => boolean, days = REMINDER_DAYS): Date[] => {
  const [h, m] = SLOTS[slot];
  const out: Date[] = [];
  for (let d = 0; out.length < days && d <= days; d++) {
    const at = new Date(now.getFullYear(), now.getMonth(), now.getDate() + d, h, m, 0, 0);
    if (at.getTime() <= now.getTime()) continue;
    if (played(utcDate(at))) continue;
    out.push(at);
  }
  return out;
};

// --- Native plugin -------------------------------------------------------------------------

const capacitor = (): any => (typeof window !== "undefined" ? (window as any).Capacitor : undefined);

const plugin = (): any => {
  const cap = capacitor();
  try {
    return cap?.Plugins?.LocalNotifications;
  } catch (e) {
    return undefined;
  }
};

let enabledForTests: boolean | undefined;
export const _setReminderEnabledForTests = (on: boolean | undefined) => {
  enabledForTests = on;
};

// True only inside the iPhone/Android app, with the native plugin built in.
export const reminderAvailable = (): boolean => {
  const enabled = enabledForTests === undefined ? DAILY_REMINDER_ENABLED : enabledForTests;
  if (!enabled || isPortal) return false;
  const cap = capacitor();
  if (!cap?.isNativePlatform?.()) return false;
  const available = typeof cap.isPluginAvailable === "function" ? cap.isPluginAvailable("LocalNotifications") : false;
  return Boolean(available && plugin());
};

const allIds = () => Array.from({ length: REMINDER_DAYS + 1 }, (_, i) => ({ id: REMINDER_ID_BASE + i }));

let channelMade = false;
const ensureChannel = async () => {
  if (channelMade || capacitor()?.getPlatform?.() !== "android") return;
  try {
    await plugin().createChannel({
      id: REMINDER_CHANNEL,
      name: t("remind.channel"),
      importance: 3, // default: sound, no heads-up pop-over
      visibility: 1,
    });
    channelMade = true;
  } catch (e) {}
};

export const cancelReminders = async () => {
  if (!reminderAvailable()) return;
  try {
    await plugin().cancel({ notifications: allIds() });
  } catch (e) {}
};

// Replace whatever is scheduled with the next week's reminders (or nothing, when off).
export const refreshReminders = async (now = new Date()): Promise<number> => {
  if (!reminderAvailable()) return 0;
  const prefs = readPrefs();
  await cancelReminders();
  if (!prefs.on) return 0;
  try {
    const perm = await plugin().checkPermissions();
    if (perm?.display !== "granted") return 0;
  } catch (e) {
    return 0;
  }
  await ensureChannel();
  const times = planReminders(now, prefs.slot, hasPlayedDaily);
  if (!times.length) return 0;
  try {
    await plugin().schedule({
      notifications: times.map((at, i) => ({
        id: REMINDER_ID_BASE + i,
        title: t("remind.title"),
        body: t("remind.body"),
        schedule: { at, allowWhileIdle: false },
        channelId: REMINDER_CHANNEL,
        smallIcon: "ic_stat_rafiki",
        extra: { route: "/daily" },
      })),
    });
    return times.length;
  } catch (e) {
    return 0;
  }
};

// The player picked a time on the offer card or switched the reminder on in the menu.
// Resolves "on", or "denied" when the phone's permission was refused.
export const turnOn = async (slot?: Slot): Promise<"on" | "denied" | "unavailable"> => {
  if (!reminderAvailable()) return "unavailable";
  const prefs = readPrefs();
  const chosen = slot || prefs.slot;
  try {
    let perm = await plugin().checkPermissions();
    if (perm?.display !== "granted") perm = await plugin().requestPermissions();
    if (perm?.display !== "granted") {
      savePrefs({ ...prefs, slot: chosen, asked: true, on: false });
      logEvent("notify_optin_denied", { slot: chosen });
      return "denied";
    }
  } catch (e) {
    return "denied";
  }
  savePrefs({ on: true, slot: chosen, asked: true });
  logEvent("notify_optin_yes", { slot: chosen });
  await refreshReminders();
  return "on";
};

export const turnOff = async () => {
  const prefs = readPrefs();
  savePrefs({ ...prefs, on: false, asked: true });
  await cancelReminders();
};

export const declineOffer = () => {
  const prefs = readPrefs();
  savePrefs({ ...prefs, asked: true });
  logEvent("notify_optin_no");
};

// Offer the reminder on the result card: in the app, after a Daily, not yet answered, not on.
export const shouldOffer = (): boolean => {
  if (!reminderAvailable()) return false;
  const p = readPrefs();
  return !p.asked && !p.on;
};

let listening = false;
// Called once when the app starts: refresh the week ahead and open /daily from a tapped reminder.
export const initReminders = (open: (route: string) => void) => {
  if (!reminderAvailable() || listening) return;
  listening = true;
  try {
    plugin().addListener("localNotificationActionPerformed", (action: any) => {
      const id = action?.notification?.id;
      if (typeof id === "number" && id >= REMINDER_ID_BASE && id <= REMINDER_ID_BASE + REMINDER_DAYS) {
        logEvent("notify_open");
        open(action?.notification?.extra?.route || "/daily");
      }
    });
  } catch (e) {}
  refreshReminders();
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") refreshReminders();
    });
  } catch (e) {}
};

// After a Daily is finished: today's reminder (if still pending) is dropped and the week ahead rescheduled.
export const onDailyPlayed = () => {
  if (!reminderAvailable()) return;
  refreshReminders();
};
